<script context="module" lang="ts">
  export type Results = {
    docs: DocumentSearchResult[];
    threads: ThreadSearchResult[];
    replies: PostSearchResult[];
  };

  function searchDocuments(usingSearchV2: boolean, communitySlug: string, searchText: string) {
    if (usingSearchV2) {
      return api.searchDocumentsV2List({
        communitySlug,
        search: searchText,
        page: 1,
      });
    }
    return api.searchDocumentsList({
      communitySlug,
      search: searchText,
      page: 1,
    });
  }

  function searchThreads(
    usingSearchV2: boolean,
    communitySlug: string,
    searchText: string,
    forum?: string
  ) {
    if (usingSearchV2) {
      return api.searchThreadsV2List({
        communitySlug,
        search: searchText,
        page: 1,
        forum,
      });
    }

    return api.searchThreadsList({
      communitySlug,
      search: searchText,
      page: 1,
      forum,
    });
  }

  function searchPosts(
    usingSearchV2: boolean,
    communitySlug: string,
    searchText: string,
    forum?: string
  ) {
    if (usingSearchV2) {
      return api.searchPostsV2List({
        communitySlug,
        search: searchText,
        page: 1,
        forum,
      });
    }

    return api.searchPostsList({
      communitySlug,
      search: searchText,
      page: 1,
      forum,
    });
  }

  function prepareBackendSearchCallback(
    communitySlug: string,
    currentSpaceUuid?: string,
    currentSpaceType?: SPACE_TYPE
  ) {
    return async (searchText: string) => {
      let forum;
      if (currentSpaceType === "forum") {
        forum = currentSpaceUuid;
      }

      const hasSemanticFlag = get(semanticFlag);
      const hasSearchV2Flag = get(searchV2Flag);
      const usingSearchV2 = hasSearchV2Flag && hasSemanticFlag;

      const knowledgeBaseEnabled = get(isDocumentsEnabled);
      const communityEnabled = get(isCommunityEnabled);

      const docsListPromise = knowledgeBaseEnabled
        ? searchDocuments(usingSearchV2, communitySlug, searchText).then(
            (docsPage) => docsPage.data.results!
          )
        : Promise.resolve([]);

      const threadsListPromise = communityEnabled
        ? searchThreads(usingSearchV2, communitySlug, searchText, forum).then(
            (threadsPage) => threadsPage.data.results!
          )
        : Promise.resolve([]);

      const postsListPromise = communityEnabled
        ? searchPosts(usingSearchV2, communitySlug, searchText, forum).then(
            (postsPage) => postsPage.data.results!
          )
        : Promise.resolve([]);

      const [docResults, threadResults, postResults] = await Promise.all<
        Array<DocumentSearchResult> | Array<ThreadSearchResult> | Array<PostSearchResult>
      >([docsListPromise, threadsListPromise, postsListPromise]);

      return {
        docs: docResults,
        threads: threadResults,
        replies: postResults,
      } as Results;
    };
  }
</script>

<script lang="ts">
  import BasicSearchAllResults from "./BasicSearchAllResults.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { searchAIFlag, communitySlug, isAskAiTeamOnly } from "app/stores/community";
  import api from "api";
  import type { SPACE_TYPE } from "app/models/spaces";
  import type {
    DocumentSearchResult,
    PostSearchResult,
    ThreadSearchResult,
  } from "@outverse-video/outverse-api-client-lib";
  import {
    isCommunityEnabled,
    isDocumentsEnabled,
    searchV2Flag,
    semanticFlag,
  } from "$stores/community";
  import { get } from "svelte/store";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import Button from "$components/+blocks/Button/Button.svelte";
  import { AI, DA, UA } from "$components/+blocks/OVIcon";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { page } from "$app/stores";
  import { isTeamMember } from "$stores/permissions";
  import { alerts } from "app/stores/alert";
  import { CanceledError } from "axios";

  export let value: string;

  let ref: HTMLInputElement;
  let dispatch = createEventDispatcher();
  let inputTimeout: NodeJS.Timeout;
  let results: Results = { docs: [], threads: [], replies: [] };

  const { spaceType } = $page.data;
  const { uuid } = $page.params;

  const resultsFn = prepareBackendSearchCallback($communitySlug, uuid, spaceType);

  $: hasResults = results.docs.length || results.threads.length || results.replies.length;
  $: canAccessSearchAI = $searchAIFlag && (!$isAskAiTeamOnly || $isTeamMember);

  onMount(() => {
    // focus input on element
    ref?.focus();
  });
</script>

<div class="search flex h-fit flex-col">
  <div class="flex items-center justify-between gap-8 py-2 pl-1 pr-2">
    <input
      bind:value
      on:keydown={(e) => {
        if (e.key === "Enter") {
          if (!canAccessSearchAI) return;
          dispatch("ask-ai");
        }
      }}
      on:input={() => {
        clearTimeout(inputTimeout);
        inputTimeout = setTimeout(async () => {
          try {
            results = await resultsFn(value);
          } catch (error) {
            if (error instanceof CanceledError) {
              alerts.error("Error fetching search results.");
            } else {
              throw error;
            }
          }
        }, 250);
      }}
      bind:this={ref}
      type="text"
      class="grow border-0 bg-transparent text-sm font-medium text-gray-500 placeholder:font-normal placeholder:text-gray-500/70 focus:outline-none focus:ring-0 dark:text-gray-300 dark:placeholder:text-gray-400/90"
      placeholder={canAccessSearchAI ? "Search or ask a question..." : "Search..."}
    />

    {#if canAccessSearchAI}
      <Button
        variant="community"
        on:click={(e) => {
          e.stopPropagation();
          if (!value) return;
          dispatch("ask-ai");
        }}
        disabled={!value}
        label="Ask AI"
        tooltipLabel={value ? "Ask AI" : "Start typing to ask AI"}
        iconRight={AI}
      />
    {/if}
  </div>

  {#if hasResults}
    <Separator />
  {/if}

  <BasicSearchAllResults {results} />

  <!-- keyboard navigation instructions -->
  {#if hasResults}
    <div class="hidden items-center justify-between px-3 pb-3 pt-5 sm:flex">
      <div class="flex items-center gap-1">
        <span
          class="flex size-[18px] items-center justify-center rounded-[3px] bg-black/10 dark:bg-white/5"
        >
          <OVIcon icon={UA} class="text-gray-500 dark:text-gray-300" size="size-2.5" />
        </span>
        <span
          class="flex size-[18px] items-center justify-center rounded-[3px] bg-black/10 dark:bg-white/5"
        >
          <OVIcon icon={DA} class="text-gray-500 dark:text-gray-300" size="size-2.5" />
        </span>
        <span class="text-xs font-medium text-neutral-500 dark:text-neutral-400">Navigate</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="text-xs font-medium text-neutral-500 dark:text-neutral-400">View</span>
        <span
          class="mt-[1px] flex size-[18px] items-center justify-center rounded-[3px] bg-black/10 text-xs text-neutral-500 dark:bg-white/5 dark:text-neutral-400"
          >↵</span
        >
      </div>
    </div>
  {/if}
</div>

<!-- hidden span to ensure tailwind classes are available for backend generated html  -->
<span hidden class="font-semibold text-community-primary" />

<!-- override mark style coming from back-end -->
<style lang="postcss">
  .search :global(mark) {
    @apply text-community-primary;
    background: none;
    font-weight: 600;
  }
</style>
